import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authMe, authLogin, authRegister, authLogout, userLogin, getStoredToken, setStoredToken, getPreferences, authGoogleDirect, authGoogleCode } from '../lib/api';
import { trackEvent } from '../lib/firebase';
import { fbTrack } from '../lib/fbpixel';
import { v4 as uuidv4 } from 'uuid';

const AuthContext = createContext(null);

function getDeviceId() {
  let id = localStorage.getItem('today_device_id');
  if (!id) {
    id = uuidv4();
    localStorage.setItem('today_device_id', id);
  }
  return id;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isGuest, setIsGuest] = useState(() => localStorage.getItem('today_is_guest') === 'true');
  const [loading, setLoading] = useState(true);
  const [deviceId] = useState(getDeviceId);
  const [preferences, setPreferences] = useState(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => localStorage.getItem(`today_onboarded_${getDeviceId()}`) === '1'
  );

  const dismissOnboarding = useCallback(() => {
    localStorage.setItem(`today_onboarded_${deviceId}`, '1');
    setOnboardingDismissed(true);
  }, [deviceId]);

  const refreshPreferences = useCallback(async () => {
    try {
      const res = await getPreferences(deviceId);
      setPreferences(res.data || null);
    } catch {
      setPreferences(null);
    } finally {
      setPrefsLoaded(true);
    }
  }, [deviceId]);

  const checkAuth = useCallback(async () => {
    // Check for guest mode
    if (localStorage.getItem('today_is_guest') === 'true') {
      setIsGuest(true);
      setUser({ name: 'Guest', guest: true, device_id: deviceId });
      setLoading(false);
      return;
    }

    const token = getStoredToken();
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await authMe();
      if (data && (data.email || data.user_id)) {
        setUser(data);
      } else {
        setUser(null);
        setStoredToken(null);
      }
    } catch {
      setUser(null);
      setStoredToken(null);
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Load preferences whenever we have an active user (authenticated or guest)
  useEffect(() => {
    if (!loading && user) {
      refreshPreferences();
    }
  }, [loading, user, refreshPreferences]);

  const needsOnboarding = (() => {
    if (loading || !user) return false;
    if (!prefsLoaded) return false;
    if (onboardingDismissed) return false;
    return true;
  })();

  const login = async (email, password) => {
    const { data } = await authLogin({ email, password, device_id: deviceId });
    if (data.token) setStoredToken(data.token);
    const userData = data.user || data;
    setUser(userData);
    setIsGuest(false);
    localStorage.removeItem('today_is_guest');
    trackEvent('login', { method: 'email' });
    return userData;
  };

  const register = async (name, email, password) => {
    const { data } = await authRegister({ name, email, password, device_id: deviceId });
    if (data.token) setStoredToken(data.token);
    const userData = data.user || data;
    setUser(userData);
    setIsGuest(false);
    localStorage.removeItem('today_is_guest');
    trackEvent('sign_up', { method: 'email' });
    fbTrack('CompleteRegistration', { status: 'email', content_name: 'Email signup' });
    return userData;
  };

  const loginAsGuest = async () => {
    try {
      await userLogin({ device_id: deviceId, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
    } catch {}
    localStorage.setItem('today_is_guest', 'true');
    setIsGuest(true);
    setUser({ name: 'Guest', guest: true, device_id: deviceId });
    trackEvent('login', { method: 'guest' });
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogleDirect = async (accessToken) => {
    const { data } = await authGoogleDirect({ access_token: accessToken, device_id: deviceId });
    if (data.token) setStoredToken(data.token);
    const userData = data.user || data;
    setUser(userData);
    setIsGuest(false);
    localStorage.removeItem('today_is_guest');
    trackEvent('login', { method: 'google' });
    fbTrack('CompleteRegistration', { status: 'google', content_name: 'Google signin' });
    return userData;
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogleCode = async (code, redirectUri) => {
    const { data } = await authGoogleCode({ code, redirect_uri: redirectUri, device_id: deviceId });
    if (data.token) setStoredToken(data.token);
    const userData = data.user || data;
    setUser(userData);
    setIsGuest(false);
    localStorage.removeItem('today_is_guest');
    trackEvent('login', { method: 'google' });
    fbTrack('CompleteRegistration', { status: 'google', content_name: 'Google signin' });
    return userData;
  };

  const setUserDirect = (userData) => {
    setUser(userData);
    setIsGuest(false);
    localStorage.removeItem('today_is_guest');
  };

  const logout = async () => {
    try { await authLogout(); } catch {}
    setStoredToken(null);
    setUser(null);
    setIsGuest(false);
    localStorage.removeItem('today_is_guest');
  };

  return (
    <AuthContext.Provider value={{ user, loading, deviceId, isGuest, login, register, loginAsGuest, loginWithGoogleDirect, loginWithGoogleCode, logout, setUserDirect, checkAuth, preferences, refreshPreferences, needsOnboarding, dismissOnboarding }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
